import { useCallback, useMemo } from 'react';
import { Vector2 } from 'three';

import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { AlignMode } from '@/modules/workspace/types/align';

import { Rectangle } from '@thive/fddm-types';
import { Template } from '@/modules/floorplanDesign/types';

import {
  ArrangeTransform,
  getAlignOffsets,
  getAreaRectangles,
  getCenter,
  getPoseTransform,
  hasAngledPath,
} from './helpers';
import { DimensionKind } from './types';
import {
  getGroupItemIds,
  selectAreasByIds,
  selectDimensionItemIds,
  selectGroupsByIds,
  selectTemplatesByIds,
  selectTopLevelSelectedIds,
} from '@/modules/floorplanDesign/selectors/selectedEntitiesSelectors';
import { offsetTemplate, rotateTemplateAround } from './templates';
import { useMoveWithTemplates } from './useMoveWithTemplates';

import {
  createItems,
  DimensionItem,
  getItemRectangles,
  offsetItem,
  toItemUpdates,
  transformItem,
} from './items';
import { isStillSelected } from './useSelectionDimensionUpdates';

type Props = {
  appMode: ReturnType<typeof useAppStore.getState>['mode'];
  kind: DimensionKind | null;
  selectedIds: string[];
};

/**
 * @description The selected areas, obstacles and logical spaces (selected groups through their
 * items), or undefined when the selection holds anything else
 */
const getSelectedItems = (selectedIds: string[]): DimensionItem[] | undefined => {
  const state = useFloorplanDesignStore.getState();
  const itemIds = selectDimensionItemIds(state, selectedIds);
  const items = createItems(
    areasToFlatCollection(state.getAreasByIds(itemIds)),
    state.getObstaclesByIds(itemIds),
    state.getLogicalSpacesByIds(itemIds),
  );
  return items.length === itemIds.length ? items : undefined;
};

/** @description Moved as one when aligning: items (one, or a group's) or a template */
type AlignBlock = { items: DimensionItem[]; template?: Template; rectangles: Rectangle[] };

/**
 * @description The selection in blocks that are aligned as one: every selected group (all its
 * items, so the group keeps its shape), every template (through its areas) and every other selected
 * item on its own. Undefined when the selection holds anything else.
 */
const getAlignBlocks = (selectedIds: string[]): AlignBlock[] | undefined => {
  const state = useFloorplanDesignStore.getState();
  const topLevelIds = selectTopLevelSelectedIds(state, selectedIds);
  const groups = selectGroupsByIds(state, topLevelIds);
  const templates = selectTemplatesByIds(state, topLevelIds);
  const containerIds = new Set([
    ...groups.map(({ id }) => id),
    ...templates.map(({ group }) => group.id),
  ]);
  const itemBlockIds = [
    ...groups.map(getGroupItemIds),
    ...topLevelIds.filter((id) => !containerIds.has(id)).map((id) => [id]),
  ];

  const itemBlocks = itemBlockIds.map((ids) => {
    const items = createItems(
      areasToFlatCollection(state.getAreasByIds(ids)),
      state.getObstaclesByIds(ids),
      state.getLogicalSpacesByIds(ids),
    );
    return { items, rectangles: items.flatMap(getItemRectangles) };
  });
  if (itemBlocks.some(({ items }, index) => items.length !== itemBlockIds[index]?.length)) {
    return undefined;
  }

  const templateBlocks = templates.map((template) => ({
    items: [],
    template,
    rectangles: selectAreasByIds(state, template.group.areaIds).flatMap(getAreaRectangles),
  }));

  return [...itemBlocks, ...templateBlocks];
};

/**
 * @description Align, rotate 90° and mirror the selection (the toolbar above the dimensions).
 * Each action is one fddm update: one generateFloorplan request and one undo step.
 */
export const useSelectionArrange = ({ appMode, kind, selectedIds }: Props) => {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const moveWithTemplates = useMoveWithTemplates(selectedIds);
  // Walls or logical spaces mixed with other items have no length, width or angle to type in (see
  // the angle_disabled tooltip), but rotating and flipping the whole selection works for them too
  const canEdit =
    appMode === 'EDITABLE' && selectedIds.length > 0 && (kind === 'items' || kind === 'restricted');
  // Templates rotate through their params (and are regenerated)...
  const canRotate = canEdit || (appMode === 'EDITABLE' && kind === 'templates');
  /*
   * A single item doesn't flip: it does nothing (vertically), equals rotating 180° (horizontally)
   * or, for a highway, what its direction button does. A selected group counts as its items. An
   * angled path (an angled highway or wall) does flip on its own: mirroring changes its shape.
   */
  const hasAngledPathSelected = useFloorplanDesignStore((state) => {
    const itemIds = selectDimensionItemIds(state, selectedIds);
    return hasAngledPath(
      areasToFlatCollection(state.getAreasByIds(itemIds)),
      state.getObstaclesByIds(itemIds),
    );
  });
  const isSingleItem = useFloorplanDesignStore(
    (state) => selectDimensionItemIds(state, selectedIds).length === 1 && !hasAngledPathSelected,
  );
  // ...but can't be mirrored: nothing in their params mirrors the generated layout.
  const flipDisabledReasonKey = (() => {
    if (kind === 'templates') return 'interface:properties.dimensions.flip_disabled_templates';
    if (isSingleItem) return 'interface:properties.dimensions.flip_disabled_single';
    return undefined;
  })();
  /*
   * Lining items up only moves each item (by its bounding box), for two or more: that works for
   * any item, also angled paths and walls with other items. A group or template moves as one block.
   */
  const canAlign =
    appMode === 'EDITABLE' &&
    (kind === 'items' || kind === 'restricted' || kind === 'templates') &&
    selectedIds.length > 1;

  const onAlign = useCallback(
    (mode: AlignMode) => {
      if (!canAlign || !isStillSelected(selectedIds)) return;

      const blocks = getAlignBlocks(selectedIds);
      if (!blocks) return;

      const offsets = getAlignOffsets(
        blocks.map(({ rectangles }) => rectangles),
        mode,
      );
      if (offsets.every((offset) => offset.lengthSq() === 0)) return;

      const alignedItems = blocks.flatMap(({ items }, index) =>
        items.map((item) => offsetItem(item, offsets[index] ?? new Vector2())),
      );
      // Only templates that actually move are regenerated
      const alignedTemplates = blocks.flatMap(({ template }, index) => {
        const offset = offsets[index];
        return template && offset && offset.lengthSq() > 0
          ? [offsetTemplate(template, offset)]
          : [];
      });

      if (alignedTemplates.length > 0) {
        moveWithTemplates(alignedItems, alignedTemplates);
        return;
      }

      fddmSvc.updateItems(toItemUpdates(alignedItems));
    },
    [canAlign, selectedIds, fddmSvc, moveWithTemplates],
  );

  /** @description Rotates the items and templates 90° clockwise around the selection centre */
  const rotateWithTemplates = useCallback(() => {
    const state = useFloorplanDesignStore.getState();
    const itemIds = selectDimensionItemIds(state, selectedIds);
    const templates = selectTemplatesByIds(state, itemIds);
    const items = createItems(
      areasToFlatCollection(state.getAreasByIds(itemIds)),
      state.getObstaclesByIds(itemIds),
      state.getLogicalSpacesByIds(itemIds),
    );
    if (items.length + templates.length !== itemIds.length) return;

    const rectangles = [
      ...items.flatMap(getItemRectangles),
      ...selectAreasByIds(
        state,
        templates.flatMap(({ group }) => group.areaIds),
      ).flatMap(getAreaRectangles),
    ];
    if (rectangles.length === 0) return;

    const center = getCenter(rectangles);
    const poseTransform = getPoseTransform('rotate90', center);
    moveWithTemplates(
      items.map((item) => transformItem(item, poseTransform)),
      // Clockwise, like rotating the items
      templates.map((template) => rotateTemplateAround(template, center, -Math.PI / 2)),
    );
  }, [moveWithTemplates, selectedIds]);

  const onTransform = useCallback(
    (transform: ArrangeTransform) => {
      if (!isStillSelected(selectedIds)) return;

      if (kind === 'templates') {
        if (canRotate && transform === 'rotate90') rotateWithTemplates();
        return;
      }

      if (!canEdit) return;

      const items = getSelectedItems(selectedIds);
      if (!items) return;
      const rectangles = items.flatMap(getItemRectangles);
      if (rectangles.length === 0) return;

      // Around the centre of the whole selection, like in Figma
      const poseTransform = getPoseTransform(transform, getCenter(rectangles));
      const transformedItems = items.map((item) => transformItem(item, poseTransform));
      const updates = toItemUpdates(transformedItems);

      fddmSvc.updateItems(updates);
    },
    [canEdit, canRotate, kind, rotateWithTemplates, selectedIds, fddmSvc],
  );

  return {
    canRotate,
    canFlip: canEdit,
    flipDisabledReasonKey,
    canAlign,
    onAlign,
    onTransform,
  };
};
